import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import type { ListParams } from '@/types/api'
import type {
  Announcement,
  AnnouncementFilters,
  AnnouncementPayload,
  AnnouncementStats,
  FeedItem,
} from './types'

export const announcementKeys = {
  all: ['announcements'] as const,
  list: (params: object) => [...announcementKeys.all, 'list', params] as const,
  stats: () => [...announcementKeys.all, 'stats'] as const,
  detail: (id: number) => [...announcementKeys.all, 'detail', id] as const,
  feedAll: ['announcements', 'feed'] as const,
  feed: (params: object) => [...announcementKeys.feedAll, 'list', params] as const,
  unread: () => [...announcementKeys.feedAll, 'unread'] as const,
}

export const announcementsQuery = (params: ListParams & AnnouncementFilters) =>
  queryOptions({
    queryKey: announcementKeys.list(params),
    queryFn: () => api.page<Announcement>('/announcements', params),
    placeholderData: keepPreviousData,
  })

export const announcementStatsQuery = () =>
  queryOptions({
    queryKey: announcementKeys.stats(),
    queryFn: () => api.get<AnnouncementStats>('/announcements/stats'),
  })

export const feedQuery = (params: { page?: number; perPage?: number; category?: string; unread?: boolean }) =>
  queryOptions({
    queryKey: announcementKeys.feed(params),
    queryFn: () =>
      api.page<FeedItem>('/me/announcements', { ...params, unread: params.unread ? 1 : undefined }),
    placeholderData: keepPreviousData,
  })

/** Unread badge; polls every minute while mounted and enabled. */
export const unreadCountQuery = (enabled: boolean) =>
  queryOptions({
    queryKey: announcementKeys.unread(),
    queryFn: () => api.get<{ unreadCount: number }>('/me/announcements/unread-count'),
    select: (d) => d.unreadCount,
    refetchInterval: 60_000,
    enabled,
  })

function useAdminMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: announcementKeys.all })
      void qc.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export const useSaveAnnouncement = () =>
  useAdminMutation(({ id, ...payload }: AnnouncementPayload & { id?: number }) =>
    id
      ? api.put<Announcement>(`/announcements/${id}`, payload)
      : api.post<Announcement>('/announcements', payload),
  )

export type LifecycleAction = 'publish' | 'unpublish' | 'expire'
export const useAnnouncementAction = () =>
  useAdminMutation(({ id, action }: { id: number; action: LifecycleAction }) =>
    api.post<Announcement>(`/announcements/${id}/${action}`),
  )
export const usePinAnnouncement = () =>
  useAdminMutation(({ id, isPinned }: { id: number; isPinned: boolean }) =>
    api.patch<Announcement>(`/announcements/${id}/pin`, { isPinned }),
  )
export const useDeleteAnnouncement = () =>
  useAdminMutation((id: number) => api.delete(`/announcements/${id}`))

/** Marks a feed item read and updates cached feed pages + the unread badge optimistically. */
export function useMarkRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.post<{ unreadCount: number }>(`/me/announcements/${id}/read`),
    onSuccess: ({ unreadCount }, id) => {
      qc.setQueryData(announcementKeys.unread(), { unreadCount })
      qc.setQueriesData<{ data: FeedItem[] }>({ queryKey: [...announcementKeys.feedAll, 'list'] }, (page) =>
        page
          ? {
              ...page,
              data: page.data.map((i) =>
                i.id === id ? { ...i, isRead: true, readAt: i.readAt ?? new Date().toISOString() } : i,
              ),
            }
          : page,
      )
    },
  })
}

export function useMarkAllRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<{ unreadCount: number }>('/me/announcements/read-all'),
    onSuccess: () => void qc.invalidateQueries({ queryKey: announcementKeys.feedAll }),
  })
}
